import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useMutation } from '@tanstack/react-query'
import type { CompanyId } from '@igf-minar/shared'
import { api } from '../lib/api'
import { pushRecentCompany } from '../lib/recent'
import { usePreferences } from '../lib/preferences'
import { useCurrentUser, isAdminRole } from '../lib/auth'
import { Icon } from '../components/layout/Icon'
import { useMessages } from '@/i18n/messages'
import { customersMessages } from '@/i18n/text/customers'

export const Route = createFileRoute('/customers')({
  component: CustomersPage,
})

const ROW_HEIGHT = 65

function CustomersPage() {
  const m = useMessages(customersMessages)
  const navigate = useNavigate()
  const { data: user } = useCurrentUser()
  const isAdmin = isAdminRole(user)
  const year = new Date().getFullYear()
  const [q, setQ] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)
  const { favorites, isFavorite, toggleFavorite } = usePreferences()
  const term = q.trim()

  const svrQuery = useQuery({
    queryKey: ['svr2-customers', term],
    queryFn: () => api.admin.svr2Customers.search(term),
    enabled: term.length >= 2,
    staleTime: 60 * 1000,
  })

  const openMut = useMutation({
    mutationFn: (kennitala: string) => api.admin.svr2Customers.open(kennitala),
    onSuccess: (res) => {
      navigate({ to: '/companies/$id', params: { id: res.companyId as CompanyId }, search: { year } })
    },
  })

  const { data, isLoading, error } = useQuery({
    queryKey: ['companies'],
    queryFn: () => api.companies(),
    staleTime: 5 * 60 * 1000,
  })

  const companies = data?.companies ?? []
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase()
    const list = term
      ? companies.filter(
          (c) =>
            c.name.toLowerCase().includes(term) ||
            c.shortName.toLowerCase().includes(term) ||
            (c.kennitala ?? '').includes(term)
        )
      : companies.filter((c) => !isAdmin || c.source !== 'svr' || favorites.includes(c.id))
    const fav = new Set(favorites)
    return [...list].sort((a, b) => {
      const fa = fav.has(a.id) ? 0 : 1
      const fb = fav.has(b.id) ? 0 : 1
      return fa !== fb ? fa - fb : a.name.localeCompare(b.name, 'is')
    })
  }, [companies, q, favorites, isAdmin])

  // Virtualize the row list so the DOM stays light even with thousands of
  // companies — only the visible window (plus a small overscan) is rendered.
  const virtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  })

  function open(c: { id: string; name: string; shortName: string; dataSource: string }) {
    if (c.dataSource !== 'live') return
    pushRecentCompany({ id: c.id, name: c.name, shortName: c.shortName })
    navigate({ to: '/companies/$id', params: { id: c.id as CompanyId }, search: { year } })
  }

  return (
    <div className="max-w-[1240px] mx-auto px-6 sm:px-10 py-8 igf-fade flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-grotesk font-bold text-[30px] leading-tight text-[var(--fg)] tracking-tight">
            {isAdmin ? m.titleAdmin : m.titleUser}
          </h1>
          <p className="mt-2 text-[15px] font-medium text-[var(--muted)]">
            {isAdmin
              ? m.subtitleAdmin(companies.length)
              : m.subtitleUser(companies.length)}
          </p>
        </div>
        <div className="flex items-center gap-2 rounded-[10px] border border-[var(--border)] bg-[var(--surface)] px-3 h-10 w-full sm:w-80 text-[var(--faint)] focus-within:border-[var(--brand)]">
          <Icon name="search" className="w-4 h-4" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={m.search}
            className="flex-1 bg-transparent text-sm text-[var(--fg)] outline-none placeholder:text-[var(--faint)]"
          />
        </div>
      </div>

      {isLoading && <div className="text-sm text-[var(--muted)]">{m.loading}</div>}
      {error && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800">
          {(error as Error).message}
        </div>
      )}

      {!isLoading && !error && (
        <div className="overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--surface)] shadow-[var(--elev)]">
          <div className="grid grid-cols-[36px_minmax(0,1fr)_140px_120px_80px] items-center bg-[var(--surface-2)] px-4 py-3 font-grotesk text-[10.5px] font-bold uppercase tracking-wider text-[var(--muted)]">
            <span />
            <span>{m.colName}</span>
            <span>{m.colKennitala}</span>
            <span>{m.colData}</span>
            <span />
          </div>

          {filtered.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-[var(--faint)]">
              {m.noneFound}
            </div>
          ) : (
            <div
              ref={scrollRef}
              className="overflow-y-auto"
              style={{ maxHeight: 'calc(100vh - 240px)' }}
            >
              <div style={{ height: virtualizer.getTotalSize(), position: 'relative', width: '100%' }}>
                {virtualizer.getVirtualItems().map((vItem) => {
                  const c = filtered[vItem.index]
                  const isLive = c.dataSource === 'live'
                  return (
                    <div
                      key={c.id}
                      onClick={() => open(c)}
                      className={`grid grid-cols-[36px_minmax(0,1fr)_140px_120px_80px] items-center border-t border-[var(--border)] px-4 text-sm transition ${
                        isLive ? 'cursor-pointer hover:bg-[var(--surface-2)]' : 'opacity-55'
                      }`}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: vItem.size,
                        transform: `translateY(${vItem.start}px)`,
                      }}
                    >
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleFavorite(c.id) }}
                        title={isFavorite(c.id) ? m.removeFavorite : m.addFavorite}
                        className={`group/star grid h-8 w-8 place-items-center rounded-lg text-xl leading-none transition ${
                          isFavorite(c.id)
                            ? 'text-[var(--brand)] hover:text-[var(--brand-strong)]'
                            : 'text-[var(--faint)] hover:bg-[var(--brand-soft)] hover:text-[var(--brand)]'
                        }`}
                      >
                        {isFavorite(c.id) ? '★' : '☆'}
                      </button>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] bg-[var(--brand-soft)] font-grotesk text-xs font-bold text-[var(--brand)]">
                          {c.shortName.charAt(0)}
                        </div>
                        <div className="min-w-0">
                          <div className="truncate font-bold text-[var(--fg)]">{c.name}</div>
                          <div className="truncate text-xs font-medium text-[var(--muted)]">{c.shortName}</div>
                        </div>
                      </div>
                      <div className="font-grotesk text-xs text-[var(--muted)]">{c.kennitala ?? '–'}</div>
                      <div>
                        <span
                          className="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                          style={
                            isLive
                              ? { background: 'var(--acc-green-soft)', color: 'var(--acc-green)' }
                              : { background: 'var(--surface-2)', color: 'var(--muted)' }
                          }
                        >
                          {isLive ? m.liveApi : m.staticData}
                        </span>
                      </div>
                      <div className="text-right">
                        {isLive && <span className="text-xs font-semibold text-[var(--brand)]">{m.open}</span>}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {term.length >= 2 && svrQuery.data && (
        <div className="overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--surface)] shadow-[var(--elev)]">
          <div className="flex items-center justify-between bg-[var(--surface-2)] px-4 py-3">
            <span className="font-grotesk text-[10.5px] font-bold uppercase tracking-wider text-[var(--muted)]">
              {m.svrHeader(svrQuery.data.total)}
            </span>
            {svrQuery.data.total > svrQuery.data.customers.length && (
              <span className="text-[10.5px] text-[var(--faint)]">{m.showingFirst(svrQuery.data.customers.length)}</span>
            )}
          </div>
          {svrQuery.data.customers.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-[var(--faint)]">
              {m.svrNoneFound}
            </div>
          ) : (
            svrQuery.data.customers.map((c) => (
              <div key={c.kennitala} className="flex items-center gap-3 border-t border-[var(--border)] px-4 py-2.5 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="truncate font-bold text-[var(--fg)]">{c.name || c.nameAlias || c.kennitala}</div>
                  <div className="font-grotesk text-xs text-[var(--muted)]">{m.kennitalaShort(c.kennitala)}</div>
                </div>
                {c.companyId ? (
                  <>
                    <button
                      onClick={() => toggleFavorite(c.companyId!)}
                      title={isFavorite(c.companyId) ? m.removeFavorite : m.addFavorite}
                      className={`grid h-8 w-8 place-items-center rounded-lg text-xl leading-none transition ${
                        isFavorite(c.companyId)
                          ? 'text-[var(--brand)] hover:text-[var(--brand-strong)]'
                          : 'text-[var(--faint)] hover:bg-[var(--brand-soft)] hover:text-[var(--brand)]'
                      }`}
                    >
                      {isFavorite(c.companyId) ? '★' : '☆'}
                    </button>
                    <button
                      onClick={() => navigate({ to: '/companies/$id', params: { id: c.companyId as CompanyId }, search: { year } })}
                      className="text-xs font-semibold text-[var(--brand)] hover:underline"
                    >
                      {m.inAppOpen}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => openMut.mutate(c.kennitala)}
                    disabled={openMut.isPending}
                    className="rounded-lg border border-[var(--border)] px-3 py-1.5 text-xs font-semibold text-[var(--fg)] hover:border-[var(--brand)] hover:text-[var(--brand)] disabled:opacity-50"
                  >
                    {openMut.isPending ? m.opening : m.fetchAndOpen}
                  </button>
                )}
              </div>
            ))
          )}
          {openMut.isError && (
            <div className="border-t border-red-200 bg-red-50 px-4 py-2 text-xs text-red-700">
              {(openMut.error as Error).message}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
